import { CREATED_AT, RULE_SET_ID, SOURCE_REFS, makeRuleTrace } from "./sourceAuthority.js";
export const NETMASTER_POINT_CAPACITY = {
    ui: 0,
    di: 0,
    ao: 0,
    do: 0,
    relay: 0
};
const METERING_GATEWAY_SELECTORS = {
    m_bus: {
        id: "selector-metering-gateway-m-bus",
        role: "metering_gateway",
        itemKind: "gateway",
        supplySource: "third_party",
        installIncluded: false,
        preferredPartNos: ["MB-10"],
        capabilityRoles: ["metering_gateway", "protocol_gateway", "m_bus_gateway"],
        requiredTextMatches: ["m-bus", "meter"],
        protocols: ["M-Bus"],
        connectivity: "Heat/HIU meters connect on M-Bus to the converter; the converter presents the metering path to the EMS/Netmaster side.",
        why: "M-Bus heat metering needs a protocol converter before the Titan EMS/Netmaster stack can use the meter data.",
        how: "Selected from the rules M-Bus gateway policy; catalog lookup must find a reviewed MB-10 or equivalent.",
        placeholderName: "M-Bus gateway - external/third-party; converts M-Bus meters onto the network",
        placeholderDescription: "Placeholder only. Use when no known product can convert M-Bus meters for the Titan/BACnet/Modbus architecture.",
        traceId: "trace-metering-cascade-m-bus-gateway",
        ruleCardId: "rule-infrastructure-metering-cascade",
        missingProductRiskRef: "open-question-mbus-gateway-product"
    },
    wireless_m_bus: {
        id: "selector-metering-gateway-wireless-m-bus",
        role: "metering_gateway",
        itemKind: "gateway",
        supplySource: "third_party",
        installIncluded: false,
        preferredPartNos: ["MB-10"],
        capabilityRoles: ["metering_gateway", "protocol_gateway", "wireless_m_bus_gateway"],
        requiredTextMatches: ["m-bus", "wireless", "meter"],
        protocols: ["wireless M-Bus"],
        connectivity: "Wireless M-Bus meters require a receiver/converter before the EMS/Netmaster side can consume meter data.",
        why: "Wireless M-Bus heat metering needs a protocol receiver/converter in the metering cascade.",
        how: "Selected from the rules wireless M-Bus gateway policy; catalog lookup must find a reviewed receiver/converter.",
        placeholderName: "Wireless M-Bus gateway - external/third-party; receives wireless M-Bus meters for the network",
        placeholderDescription: "Placeholder only. Use when no known product can receive wireless M-Bus meters for the Titan/BACnet/Modbus architecture.",
        traceId: "trace-metering-cascade-wireless-m-bus-gateway",
        ruleCardId: "rule-infrastructure-metering-cascade",
        missingProductRiskRef: "open-question-mbus-gateway-product"
    },
    modbus: {
        id: "selector-metering-gateway-modbus",
        role: "metering_gateway",
        itemKind: "gateway",
        supplySource: "titan_supplied",
        installIncluded: false,
        preferredPartNos: ["CCI-2X485/24V"],
        capabilityRoles: ["metering_gateway", "protocol_gateway", "modbus_gateway"],
        requiredTextMatches: ["modbus", "bacnet", "gateway"],
        protocols: ["Modbus RTU", "BACnet MS/TP"],
        connectivity: "Modbus RTU meter bus connects to the gateway; gateway bridges metering data onto the BACnet side.",
        why: "Modbus meter integration needs the reviewed Modbus-to-BACnet gateway.",
        how: "Selected from the rules Modbus gateway policy and resolved against catalog capabilities/protocol text.",
        placeholderName: "Modbus/BACnet gateway - external/third-party; bridges Modbus devices onto BACnet",
        placeholderDescription: "Placeholder only. Use when no existing product and no known catalog product can bridge Modbus and BACnet.",
        traceId: "trace-metering-cascade-modbus-gateway",
        ruleCardId: "rule-infrastructure-metering-cascade",
        missingProductRiskRef: "open-question-mbus-gateway-product"
    }
};
const ENERGY_METERING_SELECTOR = {
    id: "selector-energy-metering-unit",
    role: "energy_metering_unit",
    itemKind: "metering_unit",
    supplySource: "titan_supplied",
    installIncluded: false,
    preferredPartNos: ["TP-EM-16"],
    capabilityRoles: ["energy_metering_unit", "metering_unit"],
    requiredTextMatches: ["energy", "meter", "modbus", "pulsed"],
    protocols: ["BACnet/IP", "Modbus", "pulse", "0-10V", "4-20mA"],
    connectivity: "EMS receives meter data from the gateway/interface modules and publishes energy data to the supervisory network.",
    why: "Metering requirements need an energy-manager/EMS unit in the controls infrastructure.",
    how: "Selected from the rules energy-metering policy and resolved against reviewed catalog products.",
    placeholderName: "Energy metering unit - selected by metering cascade; collects and publishes meter data",
    placeholderDescription: "Placeholder only. Use when no known energy metering unit matches the metering cascade.",
    traceId: "trace-metering-cascade-energy-manager",
    ruleCardId: "rule-infrastructure-metering-cascade",
    missingProductRiskRef: "open-question-mbus-gateway-product"
};
const METER_INTERFACE_SELECTOR = {
    id: "selector-meter-interface-module",
    role: "meter_interface_module",
    itemKind: "io_module",
    supplySource: "titan_supplied",
    installIncluded: false,
    preferredPartNos: ["IO-COM-EM8"],
    capabilityRoles: ["meter_interface_module", "metering_io"],
    requiredTextMatches: ["pulsed", "meter", "input"],
    protocols: ["pulse", "0-10V", "4-20mA", "BACnet MS/TP", "Modbus"],
    connectivity: "Meter pulse/analogue endpoints terminate on the interface module; the module reports into the EMS/control network.",
    why: "Meter endpoints need a reviewed pulse/analogue input module rather than being hidden as a note.",
    how: "Quantity is one interface module per up-to-eight metering endpoints, selected from rules data and catalog capabilities.",
    placeholderName: "Meter interface module - selected by metering cascade; terminates pulse/analogue meter endpoints",
    placeholderDescription: "Placeholder only. Use when no known meter interface module matches pulse or analogue meter endpoints.",
    traceId: "trace-metering-cascade-interface-module",
    ruleCardId: "rule-infrastructure-metering-cascade",
    missingProductRiskRef: "open-question-mbus-gateway-product"
};
const MODBUS_RS485_BUS_SELECTOR = {
    id: "selector-metering-modbus-rs485-bus-note",
    role: "modbus_rs485_metering_bus",
    itemKind: "cabling_note",
    supplySource: "third_party",
    installIncluded: false,
    preferredPartNos: ["MODBUS-RTU-RS485-DAISY-CHAIN"],
    capabilityRoles: ["modbus_rs485_metering_bus", "metering_install_note"],
    requiredTextMatches: ["modbus", "rs485", "daisy"],
    protocols: ["Modbus RTU", "RS485"],
    connectivity: "By-others meters connect on a screened Modbus RTU RS485 daisy-chain directly to the apartment TP-NM-R-EM-D NetMaster.",
    commercialModel: "install_note",
    installNotes: [
        "Allow screened RS485 cabling from by-others Modbus meters to the metering NetMaster; do not convert the bus devices into IO-COM-EM8 hardwired points."
    ],
    why: "NetMaster-direct Modbus metering needs an explicit installation/topology note, not a separate protocol gateway or meter-interface module.",
    how: "Selected only when the approved metering requirement says the metering NetMaster is the Modbus RTU bus master.",
    placeholderName: "Modbus RTU RS485 meter bus - installation note for NetMaster-direct metering",
    placeholderDescription: "Placeholder only. Use when no known installation-note product exists for the Modbus RS485 metering bus.",
    traceId: "trace-metering-netmaster-direct-rs485-bus",
    ruleCardId: "rule-infrastructure-metering-cascade",
    missingProductRiskRef: "open-question-metering-topology"
};
const BMS_METERING_EXPOSURE_SELECTOR = {
    id: "selector-metering-bacnet-ip-bms-exposure",
    role: "bms_metering_exposure",
    itemKind: "other",
    supplySource: "in_house",
    installIncluded: false,
    preferredPartNos: ["NETMASTER-BACNET-IP-BMS-METERING-EXPOSURE"],
    capabilityRoles: ["bms_metering_exposure", "bacnet_ip_metering_exposure"],
    requiredTextMatches: ["netmaster", "bacnet", "bms", "metering"],
    protocols: ["BACnet/IP", "Modbus RTU"],
    connectivity: "TP-NM-R-EM-D reads the Modbus RTU meter bus and exposes the apartment meter values to the landlord BMS over BACnet/IP.",
    commercialModel: "install_note",
    installNotes: [
        "Commission BACnet/IP metering objects from the NetMaster to the landlord BMS; billing remains by others unless separately scoped."
    ],
    why: "A NetMaster-direct metering requirement that names landlord BMS exposure must surface that BACnet/IP output explicitly.",
    how: "Selected from the NetMaster-direct Modbus topology branch when metering and BACnet/IP/BMS exposure are both required.",
    placeholderName: "NetMaster BACnet/IP BMS metering exposure - commissioning/output note",
    placeholderDescription: "Placeholder only. Use when no known output/commissioning line exists for NetMaster BACnet/IP metering exposure.",
    traceId: "trace-metering-netmaster-direct-bms-exposure",
    ruleCardId: "rule-infrastructure-metering-cascade",
    missingProductRiskRef: "open-question-metering-topology"
};
const THIRD_PARTY_AV_NETWORK_SWITCH_SELECTOR = {
    id: "selector-third-party-av-network-switch",
    role: "third_party_av_network_switch",
    itemKind: "network_switch",
    supplySource: "third_party",
    installIncluded: false,
    preferredPartNos: [],
    capabilityRoles: ["third_party_av_network_switch", "industrial_ethernet_switch"],
    requiredTextMatches: ["switch"],
    protocols: ["Ethernet"],
    connectivity: "DIN-rail Ethernet switch connects the apartment Titan controls network to the third-party AV/home-automation interface point.",
    provisionScope: "apartment_type",
    commercialModel: "hardware",
    installNotes: [
        "Use an existing apartment controls network switch where already present; otherwise allow one local Ethernet switch for the AV/home-automation interface."
    ],
    why: "A third-party AV/home-automation integration needs a local apartment network switch path between Titan controls and the integrator interface.",
    how: "Selected from the third-party AV integration policy when an approved requirement names Crestron, AV integration, or home-automation interface scope.",
    placeholderName: "Apartment Ethernet switch - third-party AV/home-automation integration",
    placeholderDescription: "Placeholder only. Use when no known DIN-rail Ethernet switch matches the third-party AV integration network role.",
    traceId: "trace-third-party-av-network-switch",
    ruleCardId: "rule-third-party-av-integration",
    missingProductRiskRef: "open-question-third-party-av-switch-product"
};
const THIRD_PARTY_AV_CONNECTIVITY_LINK_SELECTOR = {
    id: "selector-third-party-av-connectivity-link",
    role: "third_party_av_connectivity_link",
    itemKind: "other",
    supplySource: "in_house",
    installIncluded: false,
    preferredPartNos: ["AUT-CRESTRON-BACNET-IP-LINK-MAPPING"],
    capabilityRoles: [
        "third_party_av_connectivity_link",
        "crestron_bacnet_ip_link",
        "commissioning_mapping_deliverable"
    ],
    requiredTextMatches: ["crestron", "bacnet", "mapping"],
    protocols: ["BACnet/IP", "Ethernet"],
    connectivity: "Titan controls expose mapped points to the Crestron/third-party AV integrator over the apartment BACnet/IP network link.",
    provisionScope: "apartment_type",
    commercialModel: "install_note",
    installNotes: [
        "Automatry provides the Crestron/third-party AV point mapping to the integrator during commissioning; this is a commissioning deliverable with no/low price impact.",
        "Confirm BACnet/IP object list, network addressing, and handover responsibility with the Crestron/AV integrator before commissioning sign-off."
    ],
    why: "A third-party AV/home-automation interface is real scope: the integrator needs a named connectivity link and point mapping, not just a note hidden in prose.",
    how: "Selected from the third-party AV integration policy when an approved requirement names Crestron, AV integration, or home-automation interface scope.",
    placeholderName: "Crestron/third-party AV BACnet/IP link and point-mapping deliverable",
    placeholderDescription: "Placeholder only. Use when no known output/commissioning line exists for the Crestron or third-party AV integration mapping deliverable.",
    traceId: "trace-third-party-av-connectivity-link",
    ruleCardId: "rule-third-party-av-integration",
    missingProductRiskRef: "open-question-third-party-av-link-mapping"
};
const LOAD_RELAY_MODULE_SELECTORS = {
    srmSp24Vac: {
        id: "selector-load-relay-srm-sp-24vac",
        role: "single_load_relay",
        itemKind: "relay",
        supplySource: "titan_supplied",
        installIncluded: false,
        preferredPartNos: ["SRM/SP/24VAC", "SRM/SP/24AC"],
        capabilityRoles: ["relay"],
        requiredTextMatches: ["srm", "single", "relay", "24v"],
        protocols: ["24V_AC"],
        connectivity: "Remote or individual high-load outputs switch through a dedicated SRM/SP/24VAC relay rather than a grouped IO/COM relay module.",
        why: "Individual towel-rail, EUFH and source-enable loads need a dedicated load relay to protect the controller output and avoid over-specifying grouped IO modules.",
        how: "Selected from Rich's accepted purpose-driven relay policy: one SRM/SP/24VAC single-pole load relay per remote or individual high-load output.",
        placeholderName: "Single-pole 24 VAC SRM load relay - selected for individual high-load outputs",
        placeholderDescription: "Placeholder only. Use when no reviewed SRM/SP/24VAC-equivalent relay product is present in the product database.",
        traceId: "trace-load-relay-srm-sp-24vac",
        ruleCardId: "rule-infrastructure-purpose-driven-load-relays",
        missingProductRiskRef: "catalog-gap-load-relay-srm-sp-24vac"
    },
    ioCom8r: {
        id: "selector-load-relay-io-com-8r",
        role: "load_relay_module",
        itemKind: "io_module",
        supplySource: "titan_supplied",
        installIncluded: false,
        preferredPartNos: ["IO/COM-8R"],
        capabilityRoles: ["load_relay_module", "relay_output_module", "io_expansion"],
        requiredTextMatches: ["relay", "8r", "io/com"],
        protocols: ["BACnet MS/TP", "Modbus"],
        connectivity: "Central load outputs terminate on the IO/COM-8R relay module in the central controls panel.",
        why: "Central load-relay outputs need a reviewed relay-output module rather than an abstract relay placeholder.",
        how: "Selected from the delivered IO/COM relay policy: pack full groups of eight outputs into IO/COM-8R modules.",
        placeholderName: "Eight-output central load-relay IO module - selected for central relay loads",
        placeholderDescription: "Placeholder only. Use when no reviewed IO/COM-8R-equivalent relay module is present in the product database.",
        traceId: "trace-load-relay-io-com-8r",
        ruleCardId: "rule-infrastructure-load-relay-io-com-modules",
        missingProductRiskRef: "catalog-gap-load-relay-io-com-8r"
    },
    ioCom4r: {
        id: "selector-load-relay-io-com-4r",
        role: "load_relay_module",
        itemKind: "io_module",
        supplySource: "titan_supplied",
        installIncluded: false,
        preferredPartNos: ["IO/COM-4R"],
        capabilityRoles: ["load_relay_module", "relay_output_module", "io_expansion"],
        requiredTextMatches: ["relay", "4r", "io/com"],
        protocols: ["BACnet MS/TP", "Modbus"],
        connectivity: "Small central load-output remainders terminate on the IO/COM-4R relay module in the central controls panel.",
        why: "A one-to-four output remainder should use the delivered small relay module rather than over-stating abstract relays.",
        how: "Selected from the delivered IO/COM relay policy: use IO/COM-4R for a small output remainder.",
        placeholderName: "Four-output central load-relay IO module - selected for small central relay remainders",
        placeholderDescription: "Placeholder only. Use when no reviewed IO/COM-4R-equivalent relay module is present in the product database.",
        traceId: "trace-load-relay-io-com-4r",
        ruleCardId: "rule-infrastructure-load-relay-io-com-modules",
        missingProductRiskRef: "catalog-gap-load-relay-io-com-4r"
    }
};
const PANEL_TIER_SELECTORS = {
    base_netmaster_panel: {
        id: "selector-panel-base-netmaster",
        role: "control_panel_enclosure",
        itemKind: "panel",
        supplySource: "titan_supplied",
        installIncluded: false,
        preferredPartNos: ["TP-NM-R-D-ENC"],
        capabilityRoles: ["control_panel_enclosure", "netmaster_panel"],
        requiredTextMatches: ["netmaster", "enclosure", "power supply"],
        protocols: ["BACnet/IP", "BACnet MS/TP"],
        connectivity: "Base residential enclosure houses Netmaster and PSU for the apartment supervisory network.",
        why: "Central kit is limited to the base residential Netmaster/PSU tier.",
        how: "Selected from the panel tier policy using central contents.",
        placeholderName: "Base Netmaster panel/enclosure - houses Netmaster and PSU",
        placeholderDescription: "Placeholder only. Use when no known panel product matches the base Netmaster enclosure tier.",
        traceId: "trace-panel-tier-base-netmaster",
        ruleCardId: "rule-infrastructure-panel-tier-selection",
        missingProductRiskRef: "open-question-panel-tier-sku-selection"
    },
    metering_netmaster_panel: {
        id: "selector-panel-metering-netmaster",
        role: "control_panel_enclosure",
        itemKind: "panel",
        supplySource: "titan_supplied",
        installIncluded: false,
        preferredPartNos: ["TP-NM-R-D-EM-ENC"],
        capabilityRoles: ["control_panel_enclosure", "metering_netmaster_panel"],
        requiredTextMatches: ["netmaster", "energy", "enclosure"],
        protocols: ["BACnet/IP", "BACnet MS/TP", "Modbus"],
        connectivity: "Metering enclosure houses the residential Netmaster with energy-monitoring infrastructure and PSU.",
        why: "Metering/EMS contents require the energy-monitoring Netmaster enclosure tier.",
        how: "Selected from the panel tier policy because metering gateway, EMS, or meter-interface modules are present.",
        placeholderName: "Metering Netmaster panel/enclosure - houses Netmaster, EMS/gateway kit, and PSU",
        placeholderDescription: "Placeholder only. Use when no known panel product matches the metering Netmaster enclosure tier.",
        traceId: "trace-panel-tier-metering-netmaster",
        ruleCardId: "rule-infrastructure-panel-tier-selection",
        missingProductRiskRef: "open-question-panel-tier-sku-selection"
    },
    expanded_controls_panel: {
        id: "selector-panel-expanded-controls",
        role: "control_panel_enclosure",
        itemKind: "panel",
        supplySource: "titan_supplied",
        installIncluded: false,
        preferredPartNos: ["TP-NM-R-D-EM-ENC"],
        capabilityRoles: ["control_panel_enclosure", "expanded_controls_panel"],
        requiredTextMatches: ["netmaster", "energy", "enclosure"],
        protocols: ["BACnet/IP", "BACnet MS/TP", "Modbus"],
        connectivity: "Expanded controls enclosure houses Netmaster/PSU plus central controllers, IO modules, gateways, and EMS as required.",
        why: "Large central contents need the highest reviewed residential enclosure tier available in the catalog.",
        how: "Selected from the panel tier policy because MZ, IO-heavy, gateway, EMS, or load-relay contents exceed the base tier.",
        placeholderName: "Expanded controls panel/enclosure - houses central controllers, IO, gateways, EMS, and relays",
        placeholderDescription: "Placeholder only. Use when no known panel product matches the expanded controls enclosure tier.",
        traceId: "trace-panel-tier-expanded-controls",
        ruleCardId: "rule-infrastructure-panel-tier-selection",
        missingProductRiskRef: "open-question-panel-tier-sku-selection"
    }
};
const EDGE_LEAK_DETECTION_SELECTOR = {
    id: "selector-edge-gateway-leak-detection",
    role: "edge_gateway",
    itemKind: "edge_compute",
    supplySource: "in_house",
    installIncluded: false,
    preferredPartNos: ["AUTO-EDGE-GATEWAY"],
    capabilityRoles: [
        "edge_gateway",
        "edge_compute",
        "leak_detection_gateway",
        "edge_cloud_dashboard",
        "alert_notification_workflow"
    ],
    requiredTextMatches: ["edge", "gateway"],
    protocols: ["BACnet/IP", "Ethernet", "HTTPS"],
    connectivity: "Self-contained Edge gateway PC bridges the apartment Titan BACnet/IP network to cloud dashboard and alert workflows.",
    provisionScope: "apartment_type",
    commercialModel: "hardware",
    installNotes: [
        "Include one self-contained Edge gateway PC with power and network uplink allowances."
    ],
    why: "An Edge leak-detection feature needs one apartment Edge gateway PC to observe Titan/BACnet data and publish leak events.",
    how: "Selected from the Edge leak-detection policy as a single AUTO-EDGE-GATEWAY line item.",
    placeholderName: "Edge gateway - apartment PC for BACnet-to-cloud leak detection",
    placeholderDescription: "Placeholder only. Use when no known Edge gateway product matches the leak-detection gateway role.",
    traceId: "trace-edge-leak-detection-gateway",
    ruleCardId: "rule-edge-leak-detection-kit",
    missingProductRiskRef: "open-question-edge-gateway-product"
};
export function deriveEdgeFeaturePolicy(input) {
    const leakFeatures = input.features.filter((feature) => feature.featureKind === "leak_detection");
    if (leakFeatures.length === 0) {
        return {
            required: false,
            sourceRequirementIds: [],
            selectors: [],
            installNotes: [],
            needsDedicatedSensorDecision: false,
            traces: []
        };
    }
    const inputPolicy = leakFeatures.some((feature) => feature.inputPolicy === "dedicated_sensors_required")
        ? "dedicated_sensors_required"
        : leakFeatures.some((feature) => feature.inputPolicy === "dedicated_sensors_to_confirm")
            ? "dedicated_sensors_to_confirm"
            : "reuse_existing_titan_inputs";
    const sourceRequirementIds = [
        ...new Set(leakFeatures.flatMap((feature) => feature.sourceRequirementIds))
    ].sort();
    const selectors = [
        {
            ...EDGE_LEAK_DETECTION_SELECTOR,
            quantity: 1
        }
    ];
    const selectorTraces = selectors.map((selector) => makeRuleTrace(selector.traceId, selector.ruleCardId, `${selector.role} selected for Edge leak detection; provision scope ${selector.provisionScope ?? "apartment_type"}.`, [SOURCE_REFS.edgeLeakDetectionRetrofit]));
    const inputTrace = makeRuleTrace("trace-edge-leak-detection-input-policy", "rule-edge-leak-input-reuse-default", inputPolicy === "reuse_existing_titan_inputs"
        ? "Leak detection defaults to reusing existing Titan/BACnet inputs; no discrete leak sensors are derived."
        : "Leak detection mentions possible dedicated sensors; keep the default Edge gateway but require a user decision before adding sensor hardware.", [SOURCE_REFS.edgeLeakDetectionRetrofit]);
    return {
        required: true,
        featureKind: "leak_detection",
        provisionUnit: "per_apartment",
        inputPolicy,
        sourceRequirementIds,
        selectors,
        installNotes: [
            "Gateway cabling/uplink must be allowed/tested by installation scope; it is not a counted catalog quantity.",
            "Default leak-detection input policy reuses the existing Titan/BACnet input estate; dedicated leak sensors require confirmation before adding hardware."
        ],
        needsDedicatedSensorDecision: true,
        traces: [...selectorTraces, inputTrace]
    };
}
export function deriveThirdPartyAvIntegrationPolicy(input) {
    if (!input.required) {
        return {
            required: false,
            protocol: input.protocol ?? "BACnet/IP",
            sourceRequirementIds: [],
            selectors: [],
            installNotes: [],
            traces: []
        };
    }
    const protocol = input.protocol?.trim() || "BACnet/IP";
    const sourceRequirementIds = [...new Set(input.sourceRequirementIds)].sort();
    const sourceRequirementTrace = sourceRequirementIds.length > 0
        ? ` Source requirement(s): ${sourceRequirementIds.join(", ")}.`
        : "";
    const selectors = [
        {
            ...THIRD_PARTY_AV_NETWORK_SWITCH_SELECTOR,
            quantity: 1,
            how: `${THIRD_PARTY_AV_NETWORK_SWITCH_SELECTOR.how}${sourceRequirementTrace}`
        },
        {
            ...THIRD_PARTY_AV_CONNECTIVITY_LINK_SELECTOR,
            quantity: 1,
            protocols: uniqueStrings([protocol, "Ethernet"]),
            connectivity: `Titan controls expose mapped points to the Crestron/third-party AV integrator over the apartment ${protocol} network link.`,
            how: `${THIRD_PARTY_AV_CONNECTIVITY_LINK_SELECTOR.how}${sourceRequirementTrace}`
        }
    ];
    const traces = selectors.map((selector) => makeRuleTrace(selector.traceId, selector.ruleCardId, `${selector.role} selected for third-party AV/home-automation integration using ${protocol}.`, [SOURCE_REFS.methodologyDecisions]));
    return {
        required: true,
        protocol,
        sourceRequirementIds,
        selectors,
        installNotes: [
            "Third-party AV/home-automation integration is requirement-driven only; do not add Crestron scope without an approved requirement.",
            "Automatry must issue the point mapping/connectivity details to the Crestron or AV integrator as a commissioning deliverable."
        ],
        traces
    };
}
export function deriveNetmasterRequirement(triggers) {
    const triggerValues = Object.values(triggers);
    const hasTrigger = triggerValues.some(Boolean);
    if (!hasTrigger) {
        return {
            quantity: 0,
            pointCapacity: NETMASTER_POINT_CAPACITY,
            requiresUserDecisionWhenNoTrigger: true,
            traces: [
                makeRuleTrace("trace-netmaster-no-hard-default", "rule-infrastructure-netmaster-zero-io", "No explicit Netmaster trigger found; hard default versus omission remains an open user decision.")
            ]
        };
    }
    return {
        quantity: 1,
        productPartNo: triggers.meteringRequired ? "TP-NM-R-EM-D" : "TP-NM-R-D",
        pointCapacity: NETMASTER_POINT_CAPACITY,
        requiresUserDecisionWhenNoTrigger: false,
        traces: [
            makeRuleTrace("trace-netmaster-triggered", "rule-infrastructure-netmaster-zero-io", "Explicit Netmaster trigger derives one Netmaster with zero hard IO.")
        ]
    };
}
export function validateNetmasterHasNoIo(points) {
    const totalPoints = points.ui + points.di + points.ao + points.do + points.relay;
    const valid = totalPoints === 0;
    return {
        id: "constraint-netmaster-zero-io",
        ruleSetId: RULE_SET_ID,
        constraintKey: "netmaster.zero_io",
        status: valid ? "pass" : "fail",
        subjectRef: "netmaster",
        message: valid
            ? "Netmaster has zero hard IO."
            : "Netmaster must not be assigned physical IO or control strategy points.",
        details: {
            points
        },
        sourceRefs: [SOURCE_REFS.baselineRules],
        ruleTraceRefs: []
    };
}
export function deriveCentralPanelRequirement(triggers) {
    const triggerPairs = [
        ["netmaster", triggers.netmasterQuantity > 0],
        ["io_module", triggers.ioModuleCount > 0],
        ["load_relay", triggers.loadRelayOutputCount > 0],
        ["mz_controller", triggers.mzControllerCount > 0],
        ["central_enable", triggers.centralEnableCount > 0],
        ["metering", triggers.meteringRequired],
        ["bms", triggers.bmsRequired],
        ["alarm_input", triggers.alarmInputCount > 0]
    ];
    const triggerKeys = triggerPairs
        .filter(([, triggered]) => triggered)
        .map(([key]) => key);
    return {
        required: triggerKeys.length > 0,
        triggerKeys,
        traces: [
            makeRuleTrace("trace-central-panel-conditional", "rule-infrastructure-central-panel-conditional", triggerKeys.length > 0
                ? `Central panel required by triggers: ${triggerKeys.join(", ")}.`
                : "No central panel trigger found; do not add a panel unconditionally.")
        ]
    };
}
export function deriveMeteringCascadePolicy(input) {
    if (!input.meteringRequired) {
        return {
            required: false,
            protocols: [],
            needsUserDecision: false,
            meterEndpointCount: 0,
            selectors: [],
            installNotes: [],
            traces: []
        };
    }
    const protocols = [...new Set(input.protocols)].sort();
    const normalizedProtocols = protocols.length > 0 ? protocols : ["modbus"];
    const topology = input.topology ??
        (input.topologyNeedsUserDecision ? "netmaster_direct" : "central_gateway");
    const meterEndpointCount = Math.max(1, input.meterEndpointCount);
    if (input.meteringHardwareInScope === false) {
        return {
            required: false,
            protocols: normalizedProtocols,
            topology,
            needsUserDecision: false,
            meterEndpointCount,
            selectors: [],
            installNotes: [
                "Metering evidence is pulse-only and/or by others; do not derive Titan metering gateway, EMS, meter-interface, or metering enclosure hardware without explicit Titan-side protocol/interface scope."
            ],
            traces: [
                makeRuleTrace("trace-metering-hardware-out-of-scope", "rule-infrastructure-metering-cascade", `Metering hardware cascade suppressed for protocols ${normalizedProtocols.join(", ")} because the approved evidence does not put Titan metering gateway/interface/EMS hardware in scope.`, [SOURCE_REFS.methodologyDecisions])
            ]
        };
    }
    const pulseOnly = normalizedProtocols.length === 1 && normalizedProtocols[0] === "pulse";
    if (pulseOnly) {
        const selectors = [
            {
                ...METER_INTERFACE_SELECTOR,
                quantity: Math.ceil(meterEndpointCount / 8)
            }
        ];
        const traces = [
            makeRuleTrace("trace-metering-pulse-netmaster-io-module", "rule-infrastructure-metering-cascade", `Pulse metering topology selected for ${meterEndpointCount} metering endpoint(s); pulse contacts terminate on IO-COM-EM8 and the NetMaster reads those IO-module inputs, so no MB-10 gateway or TP-EM-16 EMS is selected.`, [SOURCE_REFS.methodologyDecisions]),
            ...selectors.map((selector) => makeRuleTrace(selector.traceId, selector.ruleCardId, `${selector.role} selected for pulse metering IO-module to NetMaster topology and ${meterEndpointCount} metering endpoint(s).`, [SOURCE_REFS.methodologyDecisions]))
        ];
        return {
            required: true,
            protocols: normalizedProtocols,
            topology: "netmaster_direct",
            needsUserDecision: Boolean(input.topologyNeedsUserDecision),
            meterEndpointCount,
            selectors,
            installNotes: [
                "Pulse meter contacts terminate on IO-COM-EM8 inputs; the NetMaster reads those IO-module inputs as pulse metering.",
                "Do not add an MB-10 gateway, TP-EM-16 EMS, or metering enclosure for pulse-only metering unless explicit Titan-side metering hardware scope is proven."
            ],
            traces
        };
    }
    if (topology === "netmaster_direct") {
        const selectors = [
            {
                ...MODBUS_RS485_BUS_SELECTOR,
                quantity: 1
            },
            ...(input.bmsExposureRequired
                ? [
                    {
                        ...BMS_METERING_EXPOSURE_SELECTOR,
                        quantity: 1
                    }
                ]
                : [])
        ];
        const traces = [
            ...(input.topologyNeedsUserDecision
                ? [
                    makeRuleTrace("trace-metering-topology-default-modbus-netmaster", "rule-infrastructure-metering-cascade", "Metering is required but the approved inputs do not verify protocol/topology; default to Modbus-on-NetMaster and require confirmation that the meters have Modbus cards.", [SOURCE_REFS.methodologyDecisions])
                ]
                : []),
            makeRuleTrace("trace-metering-netmaster-direct-topology", "rule-infrastructure-metering-cascade", `NetMaster-direct Modbus topology selected for protocols ${normalizedProtocols.join(", ")}; TP-NM-R-EM-D is the Modbus RTU master, so no standalone gateway, TP-EM-16, IO-COM-EM8, or metering enclosure is selected.`, [SOURCE_REFS.methodologyDecisions]),
            ...selectors.map((selector) => makeRuleTrace(selector.traceId, selector.ruleCardId, `${selector.role} selected for NetMaster-direct metering topology and ${meterEndpointCount} metering endpoint(s).`, [SOURCE_REFS.methodologyDecisions]))
        ];
        return {
            required: true,
            protocols: normalizedProtocols,
            topology,
            needsUserDecision: Boolean(input.topologyNeedsUserDecision),
            meterEndpointCount,
            selectors,
            installNotes: [
                "TP-NM-R-EM-D is the Modbus RTU master for the apartment metering bus.",
                "By-others Modbus meters are bus devices; do not size IO-COM-EM8 modules for them.",
                ...(input.topologyNeedsUserDecision
                    ? [
                        "Metering assumed Modbus on NetMaster; requires Modbus cards in the meters; confirm or raise as RFI."
                    ]
                    : [])
            ],
            traces
        };
    }
    const gatewaySelectors = normalizedProtocols
        .filter((protocol) => protocol !== "pulse")
        .map((protocol) => ({
        ...METERING_GATEWAY_SELECTORS[protocol],
        quantity: 1
    }));
    const selectors = [
        ...gatewaySelectors,
        {
            ...ENERGY_METERING_SELECTOR,
            quantity: 1
        }
    ];
    const requiresHardwiredMeterInterface = normalizedProtocols.some((protocol) => ["m_bus", "wireless_m_bus", "pulse"].includes(protocol));
    if (requiresHardwiredMeterInterface) {
        selectors.push({
            ...METER_INTERFACE_SELECTOR,
            quantity: Math.ceil(meterEndpointCount / 8)
        });
    }
    const traces = selectors.map((selector) => makeRuleTrace(selector.traceId, selector.ruleCardId, `${selector.role} selected for protocols ${normalizedProtocols.join(", ")} and ${meterEndpointCount} metering endpoint(s).`, [SOURCE_REFS.methodologyDecisions]));
    return {
        required: true,
        protocols: normalizedProtocols,
        topology,
        needsUserDecision: Boolean(input.topologyNeedsUserDecision),
        meterEndpointCount,
        selectors,
        installNotes: requiresHardwiredMeterInterface
            ? [
                "Central metering cascade uses IO-COM-EM8 only for hardwired pulse/analogue-style metering endpoints; Modbus bus devices do not consume these interface points."
            ]
            : [
                "No pulse/analogue metering endpoints were identified, so no IO-COM-EM8 interface module is sized for this central metering branch."
            ],
        traces
    };
}
export function deriveLoadRelayModulePolicy(outputCount) {
    if (!Number.isInteger(outputCount) || outputCount < 0) {
        throw new Error("Central load-relay output count must be a non-negative integer.");
    }
    if (outputCount === 0) {
        return {
            outputCount,
            selectors: [],
            traces: []
        };
    }
    const fullEightCount = Math.floor(outputCount / 8);
    const remainder = outputCount % 8;
    const eightRelayModuleCount = fullEightCount + (remainder > 4 ? 1 : 0);
    const fourRelayModuleCount = remainder > 0 && remainder <= 4 ? 1 : 0;
    const selectors = [
        ...(eightRelayModuleCount > 0
            ? [
                {
                    ...LOAD_RELAY_MODULE_SELECTORS.ioCom8r,
                    quantity: eightRelayModuleCount
                }
            ]
            : []),
        ...(fourRelayModuleCount > 0
            ? [
                {
                    ...LOAD_RELAY_MODULE_SELECTORS.ioCom4r,
                    quantity: fourRelayModuleCount
                }
            ]
            : [])
    ];
    const traces = selectors.map((selector) => makeRuleTrace(selector.traceId, selector.ruleCardId, `${selector.preferredPartNos[0]} selected for ${outputCount} central load-relay output(s), selector quantity ${selector.quantity}.`, [SOURCE_REFS.deliveredLoadRelayIoComPolicy]));
    return {
        outputCount,
        selectors,
        traces
    };
}
export function deriveSingleLoadRelayPolicy(outputCount) {
    if (!Number.isInteger(outputCount) || outputCount < 0) {
        throw new Error("Single load-relay output count must be a non-negative integer.");
    }
    if (outputCount === 0) {
        return {
            outputCount,
            selectors: [],
            traces: []
        };
    }
    const selector = {
        ...LOAD_RELAY_MODULE_SELECTORS.srmSp24Vac,
        quantity: outputCount
    };
    return {
        outputCount,
        selectors: [selector],
        traces: [
            makeRuleTrace(selector.traceId, selector.ruleCardId, `${selector.preferredPartNos[0]} selected one-for-one for ${outputCount} individual high-load output(s).`, [SOURCE_REFS.baselineRules])
        ]
    };
}
export function deriveRemoteIoPanelPolicy(input) {
    if (!Number.isInteger(input.loadRelayOutputCount) || input.loadRelayOutputCount < 0) {
        throw new Error("Remote IO load-relay count must be a non-negative integer.");
    }
    if (input.loadRelayOutputCount === 0 || !input.localityCue) {
        return {
            suggested: false,
            loadRelayOutputCount: input.loadRelayOutputCount,
            installNotes: [],
            traces: []
        };
    }
    return {
        suggested: true,
        loadRelayOutputCount: input.loadRelayOutputCount,
        installNotes: [
            "Remote load relays need a local electrical feed; the relay itself does not need controller power.",
            "Each remote relay also needs a switch line from its controller: a controller DO, or a controller relay repurposed as a DO with 24V through the relay."
        ],
        traces: [
            makeRuleTrace("trace-remote-io-panel-advisory", "rule-infrastructure-remote-io-panel-advisory", `A remote/bathroom locality cue is present for ${input.loadRelayOutputCount} load-relay output(s); suggest a small remote relay enclosure for user confirmation rather than forcing allocation.`, [SOURCE_REFS.methodologyDecisions])
        ]
    };
}
export function derivePanelTierPolicy(input) {
    if (!input.centralPanel.required) {
        return {
            required: false,
            tierRank: 0,
            contents: input.contents,
            traces: input.centralPanel.traces
        };
    }
    const hasMeteringContents = input.contents.meteringGatewayCount > 0 ||
        input.contents.energyMeteringUnitCount > 0 ||
        input.contents.meterInterfaceModuleCount > 0;
    const hasExpandedContents = input.contents.mzControllerCount > 0 ||
        input.contents.ioModuleCount > 1 ||
        (input.contents.loadRelayOutputCount > 4 &&
            input.contents.centralScreenRequired !== false) ||
        hasMeteringContents;
    const tierId = hasExpandedContents
        ? hasMeteringContents
            ? "metering_netmaster_panel"
            : "expanded_controls_panel"
        : input.contents.netmasterQuantity > 0
            ? "base_netmaster_panel"
            : "custom_central_panel";
    const selector = tierId === "custom_central_panel" ? undefined : PANEL_TIER_SELECTORS[tierId];
    const trace = makeRuleTrace(selector?.traceId ?? "trace-panel-tier-custom-central", "rule-infrastructure-panel-tier-selection", `Panel tier ${tierId} selected from central contents: netmaster ${input.contents.netmasterQuantity}, MZ ${input.contents.mzControllerCount}, IO modules ${input.contents.ioModuleCount}, load relays ${input.contents.loadRelayOutputCount}, metering gateways ${input.contents.meteringGatewayCount}, EMS ${input.contents.energyMeteringUnitCount}, meter interfaces ${input.contents.meterInterfaceModuleCount}.`, [SOURCE_REFS.methodologyDecisions]);
    return {
        required: true,
        tierId,
        tierRank: tierId === "base_netmaster_panel"
            ? 1
            : tierId === "metering_netmaster_panel"
                ? 2
                : tierId === "expanded_controls_panel"
                    ? 3
                    : 4,
        contents: input.contents,
        ...(selector
            ? { selector: { ...selector, quantity: 1 } }
            : { unresolvedRiskRef: "open-question-panel-tier-sku-selection" }),
        traces: [...input.centralPanel.traces, trace]
    };
}
export function deriveTowelRailOutputs(count) {
    if (!Number.isInteger(count) || count < 0) {
        throw new Error("Towel rail count must be a non-negative integer.");
    }
    const traces = [
        makeRuleTrace("trace-towel-rail-central-load-relays", "rule-load-towel-rail-central-relay", "Towel rails are central load-relay outputs and do not consume controller zone slots.")
    ];
    return {
        towelRailCount: count,
        controllerZoneCount: 0,
        centralLoadRelayOutputCount: count,
        pointDemand: { ui: 0, di: 0, ao: 0, do: count, relay: 0 },
        infrastructure: [],
        traces
    };
}
export function makePanelInfrastructureItem(derivation) {
    if (!derivation.required) {
        return null;
    }
    return {
        id: "infrastructure-central-panel",
        itemKind: "panel",
        supplySource: "fabricated",
        installIncluded: false,
        quantity: 1,
        why: "Central panel is required because central equipment or mapped IO is present.",
        how: `Panel trigger keys: ${derivation.triggerKeys.join(", ")}.`,
        sourceRefs: [SOURCE_REFS.baselineRules],
        ruleTraceRefs: derivation.traces.map((trace) => trace.id),
        risk: {
            level: "warning",
            riskRefs: ["open-question-panel-omission-rule"],
            notes: "Panel is conditional; exact omission and sizing rules remain open for the user."
        }
    };
}
function uniqueStrings(values) {
    return [...new Set(values.filter((value) => value.trim().length > 0))];
}
export const INFRASTRUCTURE_RULES_CREATED_AT = CREATED_AT;
//# sourceMappingURL=infrastructureRules.js.map